import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type {
  Client,
  Project,
  TaskItem,
  TaskStatus,
  Contractor,
  TaskParentType,
  PreInvoiceEstimate,
  ProjectEstimateView,
} from '@bpd/shared';
import { formatCurrencyRounded, computeBottomLine, deriveProjectGates } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { TaskListDnd } from '../../components/projects/TaskListDnd.tsx';
import { TaskFormModal } from '../../components/projects/TaskFormModal.tsx';
import { LedgerTable } from '../../components/LedgerTable.tsx';
import { ProjectProductsSection } from '../../components/projects/ProjectProductsSection.tsx';
import { ProjectEstimatesSection } from '../../components/projects/ProjectEstimatesSection.tsx';
import { ProjectContractorsSection } from '../../components/projects/ProjectContractorsSection.tsx';
import { GateBadges, hasPaidButIncompleteMismatch } from '../../components/projects/GateBadges.tsx';
import {
  ProjectInvoicesSection,
  type InvoicePrefill,
  type InvoiceActivity,
} from '../../components/projects/ProjectInvoicesSection.tsx';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured' | 'not-found';
type CardView = 'short' | 'detailed';

const STATUS_LABEL: Record<TaskStatus, string> = {
  'not-started': 'Not started',
  'in-flight': 'In flight',
  complete: 'Complete',
};

const STATUS_BADGE_CLS: Record<TaskStatus, string> = {
  'not-started': 'bg-gray-100 text-gray-600',
  'in-flight': 'bg-phoenix-blue-100 text-phoenix-blue-700',
  complete: 'bg-green-100 text-green-800',
};

const DEFAULT_STATUS_FILTER: TaskStatus[] = ['not-started', 'in-flight'];

/**
 * Merges a reordered *visible* subset back into the full sibling list, keeping any
 * hidden (filtered-out) siblings in their original relative slots, then renumbers
 * `sequence` for the whole merged array — so dragging within a filtered view never
 * corrupts the true order of items the filter is hiding.
 */
function mergeReorder(full: TaskItem[], reorderedVisible: TaskItem[]): TaskItem[] {
  const visibleIds = new Set(reorderedVisible.map((i) => i.id));
  let cursor = 0;
  const merged = full.map((item) => (visibleIds.has(item.id) ? reorderedVisible[cursor++]! : item));
  return merged.map((item, idx) => ({ ...item, sequence: idx }));
}

/**
 * Resolves which index in `list` a drag ended over.
 *
 * dnd-kit's own collision detection (`event.over`) turns out to be an unreliable source of truth
 * for this: on a short/fast drag it can report `over` as the dragged item itself at drag-start and
 * never recompute before drop, leaving `event.over` `null` at the end — reproduced reliably when
 * dragging the bottom-most item up by exactly one slot. Prefer `overId` when it names a real
 * *other* item; then `fallbackOverId` (the last item-level id `handleDragOver` saw, for cases
 * where the final collision resolves to the wrapping container rather than a row); then fall back
 * to pure geometry — comparing the dragged item's actual on-screen position at drop
 * (`event.active.rect.current.translated`, always populated) against each sibling's position as
 * captured at drag-start (`rowRects`) — which needs no collision-detection event to have fired at
 * all. Only if every signal is unusable does this default to "end of list".
 */
function resolveIndex(
  list: TaskItem[],
  draggedId: string,
  overId: string | null,
  fallbackOverId: string | null,
  draggedRect: { top: number; height: number } | null,
  rowRects: Map<string, DOMRect>,
): number {
  let idx = overId && overId !== draggedId ? list.findIndex((i) => i.id === overId) : -1;
  if (idx === -1 && fallbackOverId && fallbackOverId !== draggedId) {
    idx = list.findIndex((i) => i.id === fallbackOverId);
  }
  if (idx === -1 && draggedRect) {
    const draggedCenter = draggedRect.top + draggedRect.height / 2;
    let bestDist = Infinity;
    list.forEach((item, i) => {
      const rect = rowRects.get(item.id);
      if (!rect) return;
      const dist = Math.abs(rect.top + rect.height / 2 - draggedCenter);
      if (dist < bestDist) {
        bestDist = dist;
        idx = i;
      }
    });
  }
  return idx === -1 ? list.length - 1 : idx;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

interface TaskRowProps {
  item: TaskItem;
  contractorName: string | undefined;
  siblings: TaskItem[];
  dragHandleProps: Record<string, unknown>;
  isSubtask: boolean;
  cardView: CardView;
  isExpanded?: boolean;
  subtaskCount?: number;
  total: number;
  due: number;
  onToggleExpand?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddSubtask?: () => void;
}

function TaskRow({
  item,
  contractorName,
  siblings,
  dragHandleProps,
  isSubtask,
  cardView,
  isExpanded,
  subtaskCount,
  total,
  due,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddSubtask,
}: TaskRowProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dependsOnLabels = (item.dependsOn ?? [])
    .map((id) => siblings.find((s) => s.id === id)?.item)
    .filter((label): label is string => Boolean(label));

  return (
    <div
      className={`rounded-lg border bg-white p-4 shadow-sm ${isSubtask ? 'border-gray-100' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-2">
        <div {...dragHandleProps} className="mt-0.5 shrink-0 cursor-grab touch-none select-none text-gray-300 hover:text-gray-400">
          ⠿
        </div>
        {!isSubtask && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="mt-0.5 shrink-0 text-xs text-phoenix-blue-500 hover:text-phoenix-blue-700"
            title="Show subtasks"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900">{item.item}</h3>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLS[item.status]}`}>
              {STATUS_LABEL[item.status]}
            </span>
            {!isSubtask && !!subtaskCount && (
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500">
                {subtaskCount} subtask{subtaskCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {item.description && <p className="mt-1 text-sm text-gray-600">{item.description}</p>}
          <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
            {item.date && <span>Date: {item.date}</span>}
            {contractorName && <span>Contractor: {contractorName}</span>}
            {dependsOnLabels.length > 0 && <span>Depends on: {dependsOnLabels.join(', ')}</span>}
          </p>
          {total > 0 && (
            <div className="mt-1.5 max-w-[16rem]">
              <LedgerTable
                size="sm"
                rows={[
                  { label: 'Total', value: formatCurrencyRounded(total) },
                  ...(due > 0 ? [{ label: 'Due', value: formatCurrencyRounded(due), accent: true }] : []),
                ]}
              />
            </div>
          )}
          {cardView === 'detailed' && (
            <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-400">
              {item.createdBy && <span>Added by {item.createdBy} on {formatTimestamp(item.createdAt)}</span>}
              {item.updatedBy && <span>Last edited by {item.updatedBy} on {formatTimestamp(item.updatedAt)}</span>}
            </p>
          )}
        </div>
      </div>

      {!confirmDelete ? (
        <div className="mt-2 flex items-center gap-3 border-t border-gray-100 pt-2">
          <button type="button" onClick={onEdit} className="text-sm text-gray-600 hover:underline">
            Edit
          </button>
          {onAddSubtask && (
            <button type="button" onClick={onAddSubtask} className="text-sm text-gray-600 hover:underline">
              Add subtask
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="text-sm text-red-600 hover:underline"
          >
            Delete
          </button>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2 border-t border-gray-100 pt-2">
          <span className="text-sm text-gray-700">
            Delete this {isSubtask ? 'subtask' : 'task (and its subtasks)'}?
          </span>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700"
          >
            Confirm
          </button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="text-sm text-gray-500 hover:underline">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export function ProjectDetailPage() {
  const { clientId, projectId } = useParams<{ clientId: string; projectId: string }>();
  const location = useLocation();
  const navState = location.state as
    | { from?: string; contractorId?: string; contractorName?: string; highlightEstimateId?: string }
    | null;
  const cameFrom = navState?.from;
  const cameFromDashboard = cameFrom === 'dashboard';
  const cameFromFinancials = cameFrom === 'financials';
  const cameFromContractor = cameFrom === 'contractor' && !!navState?.contractorId;
  const highlightEstimateId = navState?.highlightEstimateId;
  const [state, setState] = useState<LoadState>('loading');
  const [client, setClient] = useState<Client | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [statusFilter, setStatusFilter] = useState<Set<TaskStatus>>(new Set(DEFAULT_STATUS_FILTER));
  const [cardView, setCardView] = useState<CardView>('short');
  const [productsTotal, setProductsTotal] = useState(0);
  const [estimates, setEstimates] = useState<PreInvoiceEstimate[]>([]);
  const [taskProductTotals, setTaskProductTotals] = useState<Record<string, number>>({});
  const [invoicedTotal, setInvoicedTotal] = useState(0);
  const [paidTotal, setPaidTotal] = useState(0);
  const [invoicePrefill, setInvoicePrefill] = useState<InvoicePrefill | null>(null);
  const [invoiceActivity, setInvoiceActivity] = useState<InvoiceActivity[]>([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [estimateViews, setEstimateViews] = useState<ProjectEstimateView[]>([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [savingClosed, setSavingClosed] = useState(false);
  // Every independent project gate (see deriveProjectGates in @bpd/shared) — computed client-side
  // from data this page already holds, same inputs the Lambda's /budget rollup uses server-side for
  // ClientsPage/ClientDetailPage's compact badges, so the two can never disagree.
  const gates = useMemo(
    () =>
      deriveProjectGates({
        tasks: tasks.map((t) => ({ status: t.status, ...(t.contractorId ? { contractorId: t.contractorId } : {}) })),
        estimates: estimateViews.map((e) => ({
          isActive: e.isActive,
          ...(e.contractorId ? { contractorId: e.contractorId } : {}),
        })),
        invoices: invoiceActivity.map((a) => ({ billedAmount: a.billedAmount, paidTotal: a.paidTotal })),
        ...(project?.closedAt ? { closedAt: project.closedAt } : {}),
      }),
    [tasks, estimateViews, invoiceActivity, project?.closedAt],
  );
  const handleInvoiceTotalsChange = useCallback(({ invoicedTotal: inv, paidTotal: paid }: { invoicedTotal: number; paidTotal: number }) => {
    setInvoicedTotal(inv);
    setPaidTotal(paid);
  }, []);
  // Per-task Invoiced/Paid totals, derived from the same invoiceActivity already lifted up for the
  // page-level Budget summary — keyed by taskItemId, summed across every invoice linked to that task.
  const taskInvoiceTotals = useMemo(() => {
    const map: Record<string, { invoiced: number; paid: number }> = {};
    for (const a of invoiceActivity) {
      if (!a.taskItemId) continue;
      const entry = map[a.taskItemId] ?? { invoiced: 0, paid: 0 };
      entry.invoiced += a.billedAmount;
      entry.paid += a.paidTotal;
      map[a.taskItemId] = entry;
    }
    return map;
  }, [invoiceActivity]);
  // The project's bottom line — see computeBottomLine in @bpd/shared (also used by the Lambda's
  // /budget rollup, so ClientsPage/ClientDetailPage can never disagree with this page). '' is the
  // sentinel scope key for the task-less project-level bucket. remainingByScope (per task) feeds
  // taskBottomLine below, so each task card's Total/Due uses the exact same claim-then-pool
  // drawdown as the page-level total, not a separate computation.
  const bottomLine = useMemo(() => {
    const taskProductsSum = Object.values(taskProductTotals).reduce((a, b) => a + b, 0);
    const productByScope: Record<string, number> = { ...taskProductTotals, '': productsTotal - taskProductsSum };
    return computeBottomLine(estimates, productByScope, invoiceActivity, invoicedTotal, paidTotal);
  }, [estimates, taskProductTotals, productsTotal, invoiceActivity, invoicedTotal, paidTotal]);
  // Per-task Total/Due, mirroring the page-level bottom line but scoped to one task.
  const taskBottomLine = useMemo(() => {
    const map: Record<string, { total: number; due: number }> = {};
    const scopeKeys = new Set<string>([...Object.keys(bottomLine.remainingByScope), ...Object.keys(taskInvoiceTotals)]);
    for (const taskId of scopeKeys) {
      const invoiced = taskInvoiceTotals[taskId]?.invoiced ?? 0;
      const paid = taskInvoiceTotals[taskId]?.paid ?? 0;
      const total = (bottomLine.remainingByScope[taskId] ?? 0) + invoiced;
      map[taskId] = { total, due: total - paid };
    }
    return map;
  }, [bottomLine.remainingByScope, taskInvoiceTotals]);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [subtasksByTask, setSubtasksByTask] = useState<Record<string, TaskItem[]>>({});
  const [activeDragItem, setActiveDragItem] = useState<TaskItem | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const lastItemOverIdRef = useRef<string | null>(null);
  const rowRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const [formState, setFormState] = useState<{
    parentType: TaskParentType;
    parentId: string;
    initial?: TaskItem;
  } | null>(null);

  function load() {
    if (!clientId || !projectId) return;
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    Promise.all([
      projectsApi.listClients(),
      projectsApi.listProjects(clientId),
      projectsApi.listItems('project', projectId),
      projectsApi.listContractors(),
    ])
      .then(([clients, projects, taskList, contractorList]) => {
        const foundClient = clients.find((c) => c.id === clientId) ?? null;
        const foundProject = projects.find((p) => p.id === projectId) ?? null;
        if (!foundClient || !foundProject) {
          setState('not-found');
          return;
        }
        setClient(foundClient);
        setProject(foundProject);
        setTasks(taskList);
        setContractors(contractorList);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, [clientId, projectId]);

  async function toggleExpand(taskId: string) {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
    if (!(taskId in subtasksByTask)) {
      const subtasks = await projectsApi.listItems('task', taskId);
      setSubtasksByTask((prev) => ({ ...prev, [taskId]: subtasks }));
    }
  }

  async function expandTask(taskId: string) {
    if (expandedTasks.has(taskId)) return;
    await toggleExpand(taskId);
  }

  function handleTaskReordered(reorderedVisible: TaskItem[]) {
    const merged = mergeReorder(tasks, reorderedVisible);
    setTasks(merged);
    void projectsApi
      .reorderItems(
        'project',
        projectId!,
        merged.map((t) => ({ id: t.id, sequence: t.sequence })),
      )
      .catch((e) => console.error('Failed to persist task reorder', e));
  }

  function handleSubtaskReordered(taskId: string, reorderedVisible: TaskItem[]) {
    const full = subtasksByTask[taskId] ?? [];
    const merged = mergeReorder(full, reorderedVisible);
    setSubtasksByTask((prev) => ({ ...prev, [taskId]: merged }));
    void projectsApi
      .reorderItems(
        'task',
        taskId,
        merged.map((t) => ({ id: t.id, sequence: t.sequence })),
      )
      .catch((e) => console.error('Failed to persist subtask reorder', e));
  }

  /** Reparents a subtask across containers, then refetches just the affected lists (source, and
   * either the destination task's subtasks or the top-level task list) — simpler and less
   * bug-prone than hand-splicing three pieces of state for a rare admin action. */
  async function persistMove(
    item: TaskItem,
    fromTaskId: string,
    toParentType: TaskParentType,
    toParentId: string,
  ) {
    try {
      await projectsApi.moveItem(item.id, fromTaskId, toParentType, toParentId);
    } catch (e) {
      console.error('Failed to move item', e);
      return;
    }
    const refreshedSource = await projectsApi.listItems('task', fromTaskId);
    setSubtasksByTask((prev) => ({ ...prev, [fromTaskId]: refreshedSource }));
    if (toParentType === 'task') {
      const refreshedDest = await projectsApi.listItems('task', toParentId);
      setSubtasksByTask((prev) => ({ ...prev, [toParentId]: refreshedDest }));
      setExpandedTasks((prev) => new Set(prev).add(toParentId));
    } else {
      const refreshedTop = await projectsApi.listItems('project', projectId!);
      setTasks(refreshedTop);
    }
  }

  /** Snapshots each row's current on-screen rect (keyed by item id) so handleDragEnd can fall back
   * to pure geometry if dnd-kit's own collision-detection events turn out unusable — see
   * resolveIndex above for why that happens. Captured once at drag-start; a single-row-height of
   * drift from live drag-preview reflow by drop time is an acceptable margin for "closest row". */
  function captureRowRects(ids: string[]) {
    const map = new Map<string, DOMRect>();
    for (const id of ids) {
      const el = document.querySelector(`[data-row-id="${id}"]`);
      if (el) map.set(id, el.getBoundingClientRect());
    }
    rowRectsRef.current = map;
  }

  function handleDragStart(event: DragStartEvent) {
    lastItemOverIdRef.current = null;
    const id = String(event.active.id);
    const topLevel = tasks.find((t) => t.id === id);
    if (topLevel) {
      setActiveDragItem(topLevel);
      captureRowRects(tasks.filter((t) => statusFilter.has(t.status)).map((t) => t.id));
      return;
    }
    for (const list of Object.values(subtasksByTask)) {
      const found = list.find((t) => t.id === id);
      if (found) {
        setActiveDragItem(found);
        captureRowRects(list.filter((s) => statusFilter.has(s.status)).map((s) => s.id));
        return;
      }
    }
    setActiveDragItem(null);
  }

  // Auto-expand a collapsed top-level task when a subtask is dragged over its row, so its
  // subtask container becomes a live drop target without the admin having to expand it first.
  function handleDragOver(event: DragOverEvent) {
    const { over } = event;
    if (!over) return;
    const overId = String(over.id);
    // Remember the last *specific item* (not a container) the drag passed over — used by
    // handleDragEnd as a fallback when the final `over` resolves to a container instead (see
    // resolveIndex above).
    if (overId !== 'toplevel' && !overId.startsWith('task:')) {
      lastItemOverIdRef.current = overId;
    }
    if (!activeDragItem || activeDragItem.parentType !== 'task') return;
    if (tasks.some((t) => t.id === overId)) {
      void expandTask(overId);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const dragged = activeDragItem;
    setActiveDragItem(null);
    if (!dragged) return;
    const { over } = event;
    // `over` can be entirely null at drop even for a drag that clearly landed within the dragged
    // item's own list — dnd-kit doesn't reliably recompute collisions before pointerup on a
    // short/fast drag. Don't bail just because it's missing; resolveIndex has further fallbacks.
    const overId = over ? String(over.id) : null;
    const draggedRect = event.active.rect.current.translated;

    if (dragged.parentType === 'project') {
      // Top-level tasks only reorder within the top-level list.
      const visible = tasks.filter((t) => statusFilter.has(t.status));
      const oldIndex = visible.findIndex((t) => t.id === dragged.id);
      if (oldIndex === -1) return;
      const isToplevelTarget =
        overId === null || overId === 'toplevel' || tasks.some((t) => t.id === overId);
      if (!isToplevelTarget) return;
      const newIndex = resolveIndex(
        visible,
        dragged.id,
        overId,
        lastItemOverIdRef.current,
        draggedRect,
        rowRectsRef.current,
      );
      if (oldIndex === newIndex) return;
      handleTaskReordered(arrayMove(visible, oldIndex, newIndex));
      return;
    }

    // Dragged item is a subtask — resolve which container it was dropped on. A null `over` can
    // still be resolved as a same-container reorder if the fallback ref names a sibling subtask;
    // otherwise there's no signal to reparent onto, so it's a no-op (rarer case than the reported
    // same-container bug, and cross-container moves are just an ordinary manual re-drag away).
    const sourceTaskId = dragged.parentId;
    let targetType: TaskParentType;
    let targetParentId: string;
    if (overId === 'toplevel') {
      targetType = 'project';
      targetParentId = projectId!;
    } else if (overId !== null && tasks.some((t) => t.id === overId)) {
      targetType = 'task';
      targetParentId = overId;
    } else if (overId?.startsWith('task:')) {
      targetType = 'task';
      targetParentId = overId.slice('task:'.length);
    } else {
      const fallbackId = overId ?? lastItemOverIdRef.current;
      const ownerTaskId = fallbackId
        ? Object.entries(subtasksByTask).find(([, list]) => list.some((s) => s.id === fallbackId))?.[0]
        : undefined;
      if (!ownerTaskId) return;
      targetType = 'task';
      targetParentId = ownerTaskId;
    }

    if (targetType === 'task' && targetParentId === sourceTaskId) {
      // Same-container reorder.
      const visibleSubtasks = (subtasksByTask[sourceTaskId] ?? []).filter((s) => statusFilter.has(s.status));
      const oldIndex = visibleSubtasks.findIndex((s) => s.id === dragged.id);
      if (oldIndex === -1) return;
      const newIndex = resolveIndex(
        visibleSubtasks,
        dragged.id,
        overId,
        lastItemOverIdRef.current,
        draggedRect,
        rowRectsRef.current,
      );
      if (oldIndex === newIndex) return;
      handleSubtaskReordered(sourceTaskId, arrayMove(visibleSubtasks, oldIndex, newIndex));
      return;
    }

    void persistMove(dragged, sourceTaskId, targetType, targetParentId);
  }

  function handleSaved(saved: TaskItem) {
    if (saved.parentType === 'project') {
      setTasks((prev) => {
        const exists = prev.some((t) => t.id === saved.id);
        const next = exists ? prev.map((t) => (t.id === saved.id ? saved : t)) : [...prev, saved];
        return next.sort((a, b) => a.sequence - b.sequence);
      });
    } else {
      setSubtasksByTask((prev) => {
        const list = prev[saved.parentId] ?? [];
        const exists = list.some((t) => t.id === saved.id);
        const next = exists ? list.map((t) => (t.id === saved.id ? saved : t)) : [...list, saved];
        return { ...prev, [saved.parentId]: next.sort((a, b) => a.sequence - b.sequence) };
      });
      // A subtask was just added — make sure its parent task is expanded so it's visible.
      setExpandedTasks((prev) => new Set(prev).add(saved.parentId));
    }
    setFormState(null);
  }

  async function handleDelete(item: TaskItem) {
    await projectsApi.deleteItem(item.id, item.parentType, item.parentId);
    if (item.parentType === 'project') {
      setTasks((prev) => prev.filter((t) => t.id !== item.id));
      setSubtasksByTask((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setExpandedTasks((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } else {
      setSubtasksByTask((prev) => ({
        ...prev,
        [item.parentId]: (prev[item.parentId] ?? []).filter((t) => t.id !== item.id),
      }));
    }
  }

  function handleContractorCreated(contractor: Contractor) {
    setContractors((prev) => [...prev, contractor].sort((a, b) => a.name.localeCompare(b.name)));
  }

  function toggleStatusFilter(status: TaskStatus) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  function startEditingName() {
    setNameDraft(project?.name ?? '');
    setNameError(null);
    setEditingName(true);
  }

  async function handleSaveName() {
    if (!clientId || !projectId) return;
    if (!nameDraft.trim()) {
      setNameError('Name is required');
      return;
    }
    setSavingName(true);
    setNameError(null);
    try {
      const updated = await projectsApi.updateProject(projectId, clientId, { name: nameDraft.trim() });
      setProject(updated);
      setEditingName(false);
    } catch (e) {
      setNameError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingName(false);
    }
  }

  function startEditingDescription() {
    setDescriptionDraft(project?.description ?? '');
    setEditingDescription(true);
  }

  async function handleSaveDescription() {
    if (!clientId || !projectId) return;
    setSavingDescription(true);
    try {
      const updated = await projectsApi.updateProject(projectId, clientId, { description: descriptionDraft });
      setProject(updated);
      setEditingDescription(false);
    } finally {
      setSavingDescription(false);
    }
  }

  async function handleMarkClosed() {
    if (!clientId || !projectId) return;
    setSavingClosed(true);
    try {
      const updated = await projectsApi.updateProject(projectId, clientId, { closedAt: new Date().toISOString() });
      setProject(updated);
      setConfirmClose(false);
    } finally {
      setSavingClosed(false);
    }
  }

  async function handleReopen() {
    if (!clientId || !projectId) return;
    setSavingClosed(true);
    try {
      const updated = await projectsApi.updateProject(projectId, clientId, { closedAt: null });
      setProject(updated);
    } finally {
      setSavingClosed(false);
    }
  }

  const visibleTasks = tasks.filter((t) => statusFilter.has(t.status));

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <Link
        to={
          cameFromDashboard
            ? '/admin/dashboard'
            : cameFromFinancials
              ? '/admin/financials'
              : cameFromContractor
                ? `/admin/contractors/${navState?.contractorId}`
                : clientId
                  ? `/admin/projects/clients/${clientId}`
                  : '/admin/projects'
        }
        className="text-sm text-phoenix-blue-600 hover:underline"
      >
        ←{' '}
        {cameFromDashboard
          ? 'Dashboard'
          : cameFromFinancials
            ? 'Financial Overview'
            : cameFromContractor
              ? (navState?.contractorName ?? 'Contractor')
              : (client?.label ?? 'Client')}
      </Link>

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'not-found' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Project not found.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load this project. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && project && (
        <>
          <div className="mt-4 flex items-center justify-between">
            <div>
              {editingName ? (
                <div className="max-w-md space-y-1">
                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-2 py-1 font-serif text-2xl font-bold text-phoenix-blue-900"
                    maxLength={200}
                    autoFocus
                  />
                  {nameError && <p className="text-sm text-red-600">{nameError}</p>}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleSaveName()}
                      disabled={savingName}
                      className="rounded-md bg-phoenix-blue-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                    >
                      {savingName ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingName(false)}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <h1 className="flex items-center gap-2 font-serif text-3xl font-bold text-phoenix-blue-900">
                  {project.name}
                  <button type="button" onClick={startEditingName} className="text-xs font-normal text-gray-400 hover:underline">
                    Edit
                  </button>
                </h1>
              )}
              {editingDescription ? (
                <div className="mt-1 max-w-md space-y-1">
                  <textarea
                    value={descriptionDraft}
                    onChange={(e) => setDescriptionDraft(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700"
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleSaveDescription()}
                      disabled={savingDescription}
                      className="rounded-md bg-phoenix-blue-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                    >
                      {savingDescription ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingDescription(false)}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                  {project.description || <span className="italic text-gray-400">No description</span>}
                  <button type="button" onClick={startEditingDescription} className="text-xs text-gray-400 hover:underline">
                    Edit
                  </button>
                </p>
              )}
              <div className="mt-2 max-w-sm">
                <LedgerTable
                  rows={[
                    { label: 'Total project cost', value: formatCurrencyRounded(bottomLine.totalProjectCost), headline: true },
                    {
                      label: 'Remaining balance due',
                      value: formatCurrencyRounded(bottomLine.remainingBalanceDue),
                      accent: true,
                    },
                    ...(showBreakdown
                      ? [
                          {
                            label: 'Estimated (not yet invoiced)',
                            value: formatCurrencyRounded(bottomLine.estimatedRemaining),
                            dividerAbove: true,
                          },
                          {
                            label: 'Contractor estimates',
                            value: formatCurrencyRounded(bottomLine.contractorEstimatesShare),
                            indent: true,
                          },
                          {
                            label: 'Self-sourced materials',
                            value: formatCurrencyRounded(bottomLine.selfSourcedMaterialsShare),
                            indent: true,
                          },
                          {
                            label: 'Invoiced (awaiting payment)',
                            value: formatCurrencyRounded(invoicedTotal - paidTotal),
                          },
                          { label: 'Paid', value: formatCurrencyRounded(paidTotal) },
                        ]
                      : []),
                  ]}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowBreakdown((v) => !v)}
                className="mt-1 text-xs font-medium text-phoenix-blue-600 hover:underline"
              >
                {showBreakdown ? 'Hide breakdown' : 'See breakdown'}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setFormState({ parentType: 'project', parentId: projectId! })}
              className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
            >
              + Add task
            </button>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Status</h2>
              {project.closedAt ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    Closed {new Date(project.closedAt).toLocaleDateString()}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleReopen()}
                    disabled={savingClosed}
                    className="text-xs font-medium text-phoenix-blue-600 hover:underline disabled:opacity-50"
                  >
                    {savingClosed ? 'Saving…' : 'Reopen'}
                  </button>
                </div>
              ) : confirmClose ? (
                <div className="flex items-center gap-2">
                  {(gates.find((g) => g.key === 'workComplete')?.value === 'no' ||
                    gates.find((g) => g.key === 'paidInFull')?.value === 'no') && (
                    <span className="text-xs text-amber-700">
                      Not fully paid or not all tasks complete — close anyway?
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleMarkClosed()}
                    disabled={savingClosed}
                    className="rounded-md bg-phoenix-blue-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                  >
                    {savingClosed ? 'Saving…' : 'Confirm close'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmClose(false)}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmClose(true)}
                  className="text-xs font-medium text-gray-500 hover:underline"
                >
                  Mark closed
                </button>
              )}
            </div>
            <div className="mt-2">
              <GateBadges gates={gates} mode="full" />
            </div>
            {hasPaidButIncompleteMismatch(gates) && (
              <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <strong>Paid in full, but not done:</strong> money has been collected against this
                project, but not every task is marked complete yet.
              </p>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              {(Object.keys(STATUS_LABEL) as TaskStatus[]).map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={statusFilter.has(s)}
                    onChange={() => toggleStatusFilter(s)}
                  />
                  {STATUS_LABEL[s]}
                </label>
              ))}
            </div>
            <div className="flex overflow-hidden rounded-md border border-gray-300 text-sm">
              <button
                type="button"
                onClick={() => setCardView('short')}
                className={`px-3 py-1 ${cardView === 'short' ? 'bg-phoenix-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Short
              </button>
              <button
                type="button"
                onClick={() => setCardView('detailed')}
                className={`px-3 py-1 ${cardView === 'detailed' ? 'bg-phoenix-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Detailed
              </button>
            </div>
          </div>

          <div className="mt-6">
            {visibleTasks.length === 0 && (
              <p className="text-sm text-gray-400">No tasks match the current filter.</p>
            )}
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <TaskListDnd
                containerId="toplevel"
                items={visibleTasks}
                renderItem={(task, dragHandleProps) => {
                  const subtasks = subtasksByTask[task.id] ?? [];
                  const visibleSubtasks = subtasks.filter((s) => statusFilter.has(s.status));
                  const isExpanded = expandedTasks.has(task.id);
                  return (
                    <div key={task.id}>
                      <TaskRow
                        item={task}
                        contractorName={contractors.find((c) => c.id === task.contractorId)?.name}
                        siblings={tasks.filter((t) => t.id !== task.id)}
                        dragHandleProps={dragHandleProps}
                        isSubtask={false}
                        cardView={cardView}
                        isExpanded={isExpanded}
                        subtaskCount={subtasks.length}
                        total={taskBottomLine[task.id]?.total ?? 0}
                        due={taskBottomLine[task.id]?.due ?? 0}
                        onToggleExpand={() => void toggleExpand(task.id)}
                        onEdit={() =>
                          setFormState({ parentType: 'project', parentId: projectId!, initial: task })
                        }
                        onDelete={() => void handleDelete(task)}
                        onAddSubtask={() => setFormState({ parentType: 'task', parentId: task.id })}
                      />
                      {isExpanded && (
                        <div className="ml-8 mt-2 space-y-2 border-l-2 border-gray-100 pl-4">
                          {subtasks.length === 0 && (
                            <p className="py-1 text-xs text-gray-400">No subtasks yet.</p>
                          )}
                          {subtasks.length > 0 && visibleSubtasks.length === 0 && (
                            <p className="py-1 text-xs text-gray-400">No subtasks match the current filter.</p>
                          )}
                          <TaskListDnd
                            containerId={`task:${task.id}`}
                            items={visibleSubtasks}
                            renderItem={(subtask, subDragHandleProps) => (
                              <TaskRow
                                item={subtask}
                                contractorName={contractors.find((c) => c.id === subtask.contractorId)?.name}
                                siblings={subtasks.filter((s) => s.id !== subtask.id)}
                                dragHandleProps={subDragHandleProps}
                                isSubtask
                                cardView={cardView}
                                total={taskBottomLine[subtask.id]?.total ?? 0}
                                due={taskBottomLine[subtask.id]?.due ?? 0}
                                onEdit={() =>
                                  setFormState({ parentType: 'task', parentId: task.id, initial: subtask })
                                }
                                onDelete={() => void handleDelete(subtask)}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              <DragOverlay>
                {activeDragItem && (
                  <div className="rounded-lg border border-phoenix-blue-300 bg-white p-3 shadow-lg">
                    <p className="text-sm font-semibold text-gray-900">{activeDragItem.item}</p>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>

          <ProjectEstimatesSection
            projectId={projectId!}
            contractors={contractors}
            onContractorCreated={handleContractorCreated}
            onEstimatesChange={setEstimates}
            onEstimateViewsChange={setEstimateViews}
            onConvertToInvoice={setInvoicePrefill}
            invoiceActivity={invoiceActivity}
            {...(highlightEstimateId ? { highlightEstimateId } : {})}
          />
          <ProjectInvoicesSection
            projectId={projectId!}
            prefill={invoicePrefill}
            onInvoiceActivityChange={setInvoiceActivity}
            onPrefillConsumed={() => setInvoicePrefill(null)}
            onTotalsChange={handleInvoiceTotalsChange}
          />
          <ProjectProductsSection
            projectId={projectId!}
            contractors={contractors}
            onContractorCreated={handleContractorCreated}
            onTotalChange={setProductsTotal}
            onTaskTotalsChange={setTaskProductTotals}
            onConvertToInvoice={setInvoicePrefill}
            invoiceActivity={invoiceActivity}
          />
          <ProjectContractorsSection contractors={contractors} tasks={tasks} estimateViews={estimateViews} />
        </>
      )}

      {formState && (
        <TaskFormModal
          parentType={formState.parentType}
          parentId={formState.parentId}
          initial={formState.initial}
          siblings={
            formState.parentType === 'project'
              ? tasks.filter((t) => t.id !== formState.initial?.id)
              : (subtasksByTask[formState.parentId] ?? []).filter((t) => t.id !== formState.initial?.id)
          }
          contractors={contractors}
          onSaved={handleSaved}
          onCancel={() => setFormState(null)}
          onContractorCreated={handleContractorCreated}
        />
      )}
    </main>
  );
}
